import { useEffect, useRef, useState } from 'react';
import { Mic, X } from 'lucide-react';
import { toast } from 'sonner';

export default function VoiceJournalOverlay({ onComplete, onCancel }) {
  const [seconds, setSeconds] = useState(0);
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        const mr = new MediaRecorder(stream);
        mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
        mr.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          chunksRef.current = [];
          stream.getTracks().forEach(t => t.stop());
          if (blob.size > 200) {
            onComplete?.(blob);
          } else {
            toast.error("Recording was too short");
            onCancel?.();
          }
        };
        mediaRecorderRef.current = mr;
        mr.start();
        setRecording(true);
        timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
      } catch (e) {
        toast.error('Microphone access denied');
        onCancel?.();
      }
    })();
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        try { mediaRecorderRef.current.stop(); } catch (e) { /* ignore */ }
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, [onComplete, onCancel]);

  const stopAndSave = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  const cancelRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    chunksRef.current = [];  // ensure onstop sees an empty chunk set
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      // Stopping with empty chunks will trigger the "too short" branch
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    onCancel?.();
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center"
      data-testid="voice-journal-overlay"
    >
      <div className="bg-white rounded-3xl p-8 max-w-sm w-full mx-4 text-center shadow-2xl">
        <div className="relative w-28 h-28 mx-auto mb-4">
          <div className={`absolute inset-0 rounded-full bg-emerald-100 ${recording ? 'animate-ping opacity-60' : 'opacity-0'}`} />
          <div className="relative w-28 h-28 rounded-full bg-emerald-500 flex items-center justify-center">
            <Mic className="w-10 h-10 text-white" strokeWidth={1.5} />
          </div>
        </div>
        <p className="text-2xl font-bold tabular-nums text-gray-900 mb-2" data-testid="voice-journal-timer">
          {fmt(seconds)}
        </p>
        <p className="text-sm text-gray-500 mb-6">Recording... speak naturally about your observation.</p>
        <div className="flex gap-2">
          <button
            onClick={cancelRecording}
            className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            data-testid="voice-journal-cancel-btn"
          >
            <X className="w-4 h-4 inline mr-1" />Cancel
          </button>
          <button
            onClick={stopAndSave}
            className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors"
            data-testid="voice-journal-stop-btn"
          >
            Stop & Review
          </button>
        </div>
      </div>
    </div>
  );
}
